import type { Editor as TiptapEditor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { createClipboardHtmlSerializer } from "../clipboardHtmlSerializer";
import { Fragment, DOMParser as PMDOMParser, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { buildInlineJsonFromLines, buildPmInlineNodesFromLines } from "../tiptapInlineBuilders";
import { handleUrlPaste } from "./pasteUrlAsLink";
import { renderMarkdownForTiptapInsert } from "../renderMarkdownForTiptap";
import { isSpacerParagraph } from "../../../../src/shared/proseMirrorNodePredicates";

// CRITICAL: Please remember that when making changes to paste handling, you must also run smoke tests.
// Copy and paste from within Sudowrite, from outside sources like ChatGPT, Google Docs, MS Word, etc.
// A guide and test cases to help you to do so is here:
// https://www.notion.so/sudowrite/Copy-and-Paste-Smoke-Tests-3057975fa0ec80338876e604d9df3a05

// Detect Microsoft Word HTML/Libre Office/Google Docs and other outside sources so we can let Prose Mirror handle it.
const WORD_HTML_PATTERN = /<!--\s*\[if gte mso|class=(['"])[\s\S]*?\bMso|style=(['"])[\s\S]*?\bmso-/i;
const FALLBACK_HTML_SELECTORS = ["[class*='Mso']", "[class*='OutlineElement']", "[class*='SCXW']", "[style*='mso-']"];
const FALLBACK_HTML_PATTERNS = [
  /<span[^>]+\blang=/i,
  /style=(['"])[^'"]*\bmargin-bottom:\s*0in/i,
  /style=(['"])[^'"]*\bline-height:\s*100%/i,
  /style=(['"])[^'"]*\bmargin-bottom:\s*0pt/i,
  /class=(['"])\s*[^'"]*\bOutlineElement\b/i,
  /class=(['"])\s*[^'"]*\bSCXW/i,
  /docs-internal-guid/i,
];
const INTERNAL_CUT_CLIPBOARD_TYPE = "application/x-sudowrite-internal-cut";
const INTERNAL_CUT_CLIPBOARD_VALUE = "1";

type ProseMirrorJSONContent = {
  type: string;
  text?: string;
  content?: ProseMirrorJSONContent[];
};

type ParagraphNode = {
  type: "paragraph";
  content: ProseMirrorJSONContent[];
};

const matchesFallbackSelector = (dom: Document | null): boolean => {
  if (!dom) return false;
  for (let i = 0; i < FALLBACK_HTML_SELECTORS.length; i += 1) {
    const selector = FALLBACK_HTML_SELECTORS[i];
    if (!selector) continue;
    try {
      if (dom.querySelector(selector)) return true;
    } catch (_) {
      // Ignore invalid selectors so one bad entry doesn't break paste.
    }
  }
  return false;
};

export const shouldDeferToNativeHandling = (html: string, dom: Document | null): boolean => {
  return (
    WORD_HTML_PATTERN.test(html) ||
    matchesFallbackSelector(dom) ||
    FALLBACK_HTML_PATTERNS.some((re) => {
      try {
        return re.test(html);
      } catch (_) {
        return false;
      }
    })
  );
};

const RICH_FORMATTING_SELECTOR = "h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,pre,code,strong,b,em,i,u,s,strike,del,a,table,mark,img";

/**
 * True when the clipboard HTML carries actual formatting. Formatting-free
 * HTML (just p/div/span/br wrappers around text — e.g. chat copy output or
 * plain-text-ish sources) is a signal that any markdown syntax in the text
 * is meant as markdown, not literal characters.
 */
export const htmlHasRichFormatting = (dom: Document | null): boolean => {
  try {
    return !!dom?.body?.querySelector(RICH_FORMATTING_SELECTOR);
  } catch (_) {
    return true;
  }
};

/**
 * Markdown treats a single newline as a soft wrap (joined with a space), but
 * the editor's plain-text paste has always kept it as a visible line break.
 * Appending two trailing spaces (markdown's hard-break syntax) preserves that
 * for lines that continue a paragraph; it is inert at block boundaries
 * (headings, list items, blank lines) and skipped inside code fences.
 */
const preserveSoftLineBreaksForMarkdown = (text: string): string => {
  const lines = text.split("\n");
  let inFence = false;
  const isHeadingOrListMarkerLine = (line: string): boolean =>
    /^\s{0,3}#{1,6}(?:\s|$)/.test(line) || /^\s*(?:[-*+]|\d+\.)\s+/.test(line);

  return lines
    .map((line, i) => {
      if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
      if (inFence) return line;
      const next = lines[i + 1];
      if (
        line.trim().length === 0
        || next === undefined
        || next.trim().length === 0
        || isHeadingOrListMarkerLine(line)
      ) {
        return line;
      }
      return `${line}  `;
    })
    .join("\n");
};

/**
 * The manuscript schema has no orderedList node — numbered lists live as
 * literal "1. " text (the same convention copyUtils uses for card copy).
 * Rewrites rendered-markdown <ol>s into numbered paragraphs; without this,
 * ProseMirror drops the unknown <ol> tag, the floating <li>s get rewrapped
 * as a bulletList, and the numbering is silently lost. Returns undefined for
 * shapes the literal convention can't hold (nested or non-top-level lists,
 * multi-paragraph items) so the paste falls back to plain text.
 */
const convertOrderedListsToNumberedParagraphs = (html: string): string | undefined => {
  if (!/<ol\b/i.test(html)) return html;
  if (typeof document === "undefined") return undefined;

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;

  const lists = Array.from(wrapper.querySelectorAll("ol"));
  for (const ol of lists) {
    if (ol.parentElement !== wrapper || ol.querySelector("ol, ul")) return undefined;

    const start = Number.parseInt(ol.getAttribute("start") || "1", 10) || 1;
    const items = Array.from(ol.children).filter((el) => el.tagName === "LI");
    const paragraphs: HTMLElement[] = [];

    for (let i = 0; i < items.length; i += 1) {
      const li = items[i];
      if (li.querySelectorAll("p").length > 1) return undefined;
      // Tight items hold inline content directly; loose items wrap it in a
      // single <p>. Unwrap the latter so the number shares the line.
      const onlyChild = li.children.length === 1 ? li.children[0] : null;
      const source = onlyChild?.tagName === "P" ? onlyChild : li;

      const paragraph = document.createElement("p");
      paragraph.appendChild(document.createTextNode(`${start + i}. `));
      while (source.firstChild) paragraph.appendChild(source.firstChild);
      paragraphs.push(paragraph);
    }

    ol.replaceWith(...paragraphs);
  }

  return wrapper.innerHTML;
};

/**
 * Renders likely-markdown pasted text to HTML for TipTap to parse into real
 * headings/lists/marks. Ordered lists become literal "1. " numbered
 * paragraphs (no orderedList in the manuscript schema). Skips output
 * containing elements the schema can't represent (e.g. <hr> from a "* * *"
 * scene separator) so that content falls back to plain-text handling instead
 * of being dropped.
 */
export const renderPastedMarkdownHtml = (text: string): string | undefined => {
  const html = renderMarkdownForTiptapInsert(preserveSoftLineBreaksForMarkdown(text));
  if (!html || /<hr\b/i.test(html)) return undefined;
  return convertOrderedListsToNumberedParagraphs(html);
};

const trimEdgeEmptyParagraphs = (slice: Slice): Slice => {
  try {
    const nodes: any[] = [];
    for (let i = 0; i < slice.content.childCount; i += 1) {
      nodes.push(slice.content.child(i));
    }

    const isEmptyParagraph = (node: any) => node.type && node.type.name === "paragraph" && node.content && node.content.size === 0;

    while (nodes.length > 0 && isEmptyParagraph(nodes[0])) nodes.shift();
    while (nodes.length > 0 && isEmptyParagraph(nodes[nodes.length - 1])) nodes.pop();

    if (nodes.length === 0) return slice;
    const newContent = Fragment.fromArray(nodes);
    return new Slice(newContent, slice.openStart, slice.openEnd);
  } catch (_) {
    return slice;
  }
};

const isHardBreak = (node: any) => node.type?.name === "hardBreak";

/**
 * Drops spacing-as-content from pasted slices: trailing hardBreak runs inside
 * top-level paragraphs (the `<p>text<br></p>` clipboard dialect used by some
 * external sources and old chat/card copies) and spacer paragraphs (empty,
 * whitespace/NBSP-only, hardBreak-only, or mixed blank inline content).
 * Visual paragraph separation comes from the Paragraph Spacing setting
 * instead. Mid-paragraph soft breaks are preserved.
 *
 * Open-edge handling: a node whose END is open merges into the remainder of
 * the cursor paragraph on paste, so its trailing breaks may legitimately end
 * up mid-paragraph — leave them. An open START only affects a node's left
 * edge; when more blocks follow, its trailing breaks still sit right before
 * a paragraph split and are safe to strip. Open-edge blank paragraphs are
 * kept (never dropped) because removing them would shift the slice's open
 * depth onto a neighboring node and merge away a real paragraph boundary.
 */
export const stripSpacingArtifactsFromSlice = (slice: Slice): Slice => {
  try {
    const nodes: any[] = [];
    let changed = false;

    for (let i = 0; i < slice.content.childCount; i += 1) {
      let node = slice.content.child(i);
      const isOpenStartEdge = i === 0 && slice.openStart > 0;
      const isOpenEndEdge = i === slice.content.childCount - 1 && slice.openEnd > 0;

      if (!isOpenEndEdge && node.type?.name === "paragraph") {
        let keepCount = node.content.childCount;
        while (keepCount > 0 && isHardBreak(node.content.child(keepCount - 1))) keepCount -= 1;
        if (keepCount !== node.content.childCount) {
          const kept: any[] = [];
          for (let k = 0; k < keepCount; k += 1) kept.push(node.content.child(k));
          node = node.copy(Fragment.fromArray(kept));
          changed = true;
        }
        if (isSpacerParagraph(node) && !isOpenStartEdge) {
          changed = true;
          continue;
        }
      }

      nodes.push(node);
    }

    if (!changed || nodes.length === 0) return slice;
    return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
  } catch (_) {
    return slice;
  }
};

const isListBlock = (node: any) => ["bulletList", "orderedList", "taskList"].includes(node?.type?.name);
const isListItem = (node: any) => node?.type?.name === "listItem" || node?.type?.name === "taskItem";

/**
 * Keeps pasted lists whole. Two parseSlice shapes drop items on
 * replaceSelection when the cursor is in a plain paragraph:
 *
 * - A slice that is nothing but bare `listItem` nodes (openStart/openEnd
 *   reaching through an implied open list parent). This is what `<ol>` HTML
 *   parses to when the schema has no orderedList node: the unknown `<ol>` tag
 *   is skipped and the `<li>`s float. Fitting merges the FIRST item's content
 *   into the cursor paragraph — the "first scene becomes a bare paragraph"
 *   bug. Rewrap the items in a real bulletList and close the slice.
 *
 * - A slice whose edge node is a real list with an open edge reaching into
 *   its first/last item; closing the edge keeps that item inside the list.
 *
 * Only used when the selection is NOT inside a list — pasting list items into
 * an existing list should keep merging items into it (open edges are correct
 * there).
 */
export const closeSliceEdgesAroundLists = (slice: Slice, schema: any): Slice => {
  try {
    let allListItems = slice.content.childCount > 0;
    for (let i = 0; i < slice.content.childCount; i += 1) {
      if (!isListItem(slice.content.child(i))) {
        allListItems = false;
        break;
      }
    }
    if (allListItems && schema?.nodes?.bulletList) {
      const listType = slice.content.firstChild?.type.name === "taskItem" ? schema.nodes.taskList : schema.nodes.bulletList;
      const list = listType.createChecked(null, slice.content);
      return new Slice(Fragment.from(list), 0, 0);
    }

    let { openStart, openEnd } = slice;
    if (openStart > 0 && isListBlock(slice.content.firstChild)) openStart = 0;
    if (openEnd > 0 && isListBlock(slice.content.lastChild)) openEnd = 0;
    if (openStart === slice.openStart && openEnd === slice.openEnd) return slice;
    return new Slice(slice.content, openStart, openEnd);
  } catch (_) {
    return slice;
  }
};

export const selectionWithinList = (state: any): boolean => {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if (isListBlock($from.node(depth))) return true;
  }
  return false;
};

export const transformPastedSliceForClipboard = (slice: Slice, preserveAnnotationMarks: boolean): Slice => {
  const trimmed = trimEdgeEmptyParagraphs(slice);
  // Internal cut/paste round-trips content exactly; everything else sheds
  // annotation marks and spacing-as-content artifacts.
  if (preserveAnnotationMarks) return trimmed;
  return stripSpacingArtifactsFromSlice(stripAnnotationMarksFromSlice(trimmed));
};

export const stripAnnotationMarksFromSlice = (slice: Slice): Slice => {
  let changed = false;

  const stripNode = (node: any): any => {
    let content = node.content;
    let nodeChanged = false;

    if (content?.childCount > 0) {
      const children: any[] = [];
      for (let i = 0; i < content.childCount; i += 1) {
        const child = content.child(i);
        const strippedChild = stripNode(child);
        children.push(strippedChild);
        if (strippedChild !== child) nodeChanged = true;
      }
      if (nodeChanged) {
        content = Fragment.fromArray(children);
      }
    }

    const marks = node.marks || [];
    const filteredMarks = marks.filter((mark: any) => mark.type?.name !== "annotation");
    if (filteredMarks.length !== marks.length) nodeChanged = true;

    if (!nodeChanged) return node;

    changed = true;
    const nextNode = content !== node.content ? node.copy(content) : node;
    if (filteredMarks.length !== marks.length) {
      return nextNode.mark(filteredMarks);
    }
    return nextNode;
  };

  const nodes: any[] = [];
  for (let i = 0; i < slice.content.childCount; i += 1) {
    const child = slice.content.child(i);
    const strippedChild = stripNode(child);
    nodes.push(strippedChild);
    if (strippedChild !== child) changed = true;
  }

  if (!changed) return slice;
  return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
};

export const shouldPreserveAnnotationMarksFromClipboardData = (clipboardData?: DataTransfer | null): boolean => {
  try {
    return clipboardData?.getData(INTERNAL_CUT_CLIPBOARD_TYPE) === INTERNAL_CUT_CLIPBOARD_VALUE;
  } catch (_) {
    return false;
  }
};

const markClipboardAsInternalCut = (clipboardData?: DataTransfer | null): void => {
  try {
    clipboardData?.setData(INTERNAL_CUT_CLIPBOARD_TYPE, INTERNAL_CUT_CLIPBOARD_VALUE);
  } catch (_) {
    // Some clipboard implementations reject custom MIME types. In that case,
    // pasted annotations fall back to copy semantics and are stripped.
  }
};

const writeSelectionToInternalCutClipboard = (view: any, event: ClipboardEvent): boolean => {
  const selection = view?.state?.selection;
  const clipboardData = event.clipboardData;
  if (!selection || selection.empty || !clipboardData || !view?.serializeForClipboard) return false;

  const slice = selection.content();
  const { dom, text } = view.serializeForClipboard(slice);

  event.preventDefault();
  clipboardData.clearData();
  markClipboardAsInternalCut(clipboardData);
  clipboardData.setData("text/html", dom.innerHTML);
  clipboardData.setData("text/plain", text);
  view.dispatch(view.state.tr.deleteSelection().scrollIntoView().setMeta("uiEvent", "cut"));
  return true;
};

/**
 * Blank lines (2+ consecutive `\n`) are paragraph boundaries; single `\n` is a
 * soft break within a paragraph. No spacer empty paragraphs are manufactured —
 * visual separation between paragraphs comes from the user's Paragraph Spacing
 * setting (margin-bottom via --sw-paragraph-spacing), not document structure.
 */
export function buildParagraphNodesFromPlainText(_editor: TiptapEditor, text: string): ParagraphNode[] {
  const normalized = String(text).replace(/\r\n/g, "\n");

  const nodes: ParagraphNode[] = normalized
    .split(/\n{2,}/)
    .map((chunk) => chunk.split("\n").filter((line) => line.length > 0))
    .filter((lines) => lines.length > 0)
    .map((lines) => ({
      type: "paragraph",
      content: buildInlineJsonFromLines(lines),
    }));

  if (nodes.length === 0) return [{ type: "paragraph", content: [] }];
  return nodes;
}

/**
 * Pastes text into an inline context (mid-paragraph or over a selection): the
 * first chunk is inserted inline at the selection, and any further blank-line
 * separated chunks become new paragraphs. Like buildParagraphNodesFromPlainText,
 * no spacer empty paragraphs are created between them.
 */
function insertInlineExact(editor: TiptapEditor, text: string): void {
  if (!editor?.chain) return;
  const normalized = String(text).replace(/\r\n/g, "\n").replace(/^\n+/, "").replace(/\n+$/, "");

  const chunks = normalized.split(/\n{2,}/);
  const firstLines = chunks[0].split("\n");
  const pmInline = buildPmInlineNodesFromLines(editor, firstLines);
  const fragment = Fragment.fromArray(pmInline);
  const slice = new Slice(fragment, 0, 0);
  const tr = editor.state.tr.replaceSelection(slice);
  editor.view.dispatch(tr);

  const nodes: ParagraphNode[] = chunks.slice(1).map((chunk) => ({
    type: "paragraph",
    content: buildInlineJsonFromLines(chunk.split("\n")),
  }));
  if (nodes.length > 0) editor.chain().focus().insertContent(nodes).run();
}

const CF_HTML_BOUNDARY_REGEX = /StartFragment:(\d+)/i;
const CF_HTML_END_FRAGMENT_REGEX = /EndFragment:(\d+)/i;
const CF_HTML_START_HTML_REGEX = /StartHTML:(\d+)/i;
const CF_HTML_END_HTML_REGEX = /EndHTML:(\d+)/i;

const extractClipboardHtmlFragment = (payload?: string | null): string => {
  if (!payload) return "";
  let start = -1;
  let end = -1;

  const startMarker = payload.indexOf("<!--StartFragment-->");
  const endMarker = payload.indexOf("<!--EndFragment-->");
  if (startMarker !== -1 && endMarker !== -1) {
    start = startMarker + "<!--StartFragment-->".length;
    end = endMarker;
  }

  if (start === -1 || end === -1) {
    const startMatch = payload.match(CF_HTML_BOUNDARY_REGEX);
    const endMatch = payload.match(CF_HTML_END_FRAGMENT_REGEX);

    if (startMatch && endMatch) {
      start = parseInt(startMatch[1], 10);
      end = parseInt(endMatch[1], 10);
    } else {
      const startHtmlMatch = payload.match(CF_HTML_START_HTML_REGEX);
      const endHtmlMatch = payload.match(CF_HTML_END_HTML_REGEX);
      if (startHtmlMatch && endHtmlMatch) {
        start = parseInt(startHtmlMatch[1], 10);
        end = parseInt(endHtmlMatch[1], 10);
      }
    }
  }

  if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end <= start || end > payload.length) {
    const htmlIndex = payload.toLowerCase().indexOf("<html");
    if (htmlIndex >= 0) {
      return payload.slice(htmlIndex);
    }
    const bodyIndex = payload.toLowerCase().indexOf("<body");
    if (bodyIndex >= 0) {
      return payload.slice(bodyIndex);
    }
    return payload;
  }

  let fragment = payload.slice(start, end);
  fragment = fragment
    .replace(/^[\s-]*StartFragment-->/i, "")
    .replace(/<!--EndFragment-->\s*$/i, "")
    .replace(/<!--StartFragment-->/gi, "")
    .replace(/<!--EndFragment-->/gi, "");
  return fragment;
};

const getClipboardParser = (view: any): PMDOMParser => {
  let parser: PMDOMParser | null = null;
  view.someProp("clipboardParser", (propParser) => {
    parser = propParser;
    return true;
  });
  if (!parser) {
    parser = PMDOMParser.fromSchema(view.state.schema);
  }
  return parser;
};

/** Recognize standard checkbox-list HTML from Markdown editors before schema parsing. */
const normalizeClipboardTaskLists = (root: HTMLElement): void => {
  root.querySelectorAll("ul:not([data-type])").forEach(list => {
    const items = Array.from(list.children);
    const checkboxes = items.map(item => {
      if (item.tagName !== "LI") return null;
      const first = item.firstElementChild;
      const container = first?.tagName === "P" ? first : item;
      const checkbox = container.firstElementChild;
      if (!checkbox?.matches('input[type="checkbox"]')) return null;
      // Only a leading checkbox is a task marker, not a checkbox embedded in prose.
      for (let node = container.firstChild; node && node !== checkbox; node = node.nextSibling) {
        if (node.textContent?.trim()) return null;
      }
      return checkbox as HTMLInputElement;
    });
    // Do not reinterpret an ordinary/mixed bullet list as a checklist.
    if (!items.length || checkboxes.some(checkbox => !checkbox)) return;
    list.setAttribute("data-type", "taskList");
    items.forEach((item, index) => {
      item.setAttribute("data-type", "taskItem");
      item.setAttribute("data-checked", String(checkboxes[index]!.checked));
    });
  });
};

// Apply the creation gate at the clipboard boundary, not in the reader schema.
// Internal cuts move existing content and must retain checkboxes and annotations.
export const gatePastedTaskLists = (slice: Slice, schema: any, canWrite: boolean, internalCut: boolean): Slice => {
  if (canWrite || internalCut) return slice;
  const convert = (node: any): any => {
    if (node.isText) return node;
    const children: any[] = [];
    node.forEach((child: any) => children.push(convert(child)));
    const content = Fragment.fromArray(children);
    if (node.type.name === "taskList") return schema.nodes.bulletList.create(null, content);
    if (node.type.name === "taskItem") return schema.nodes.listItem.create(null, content);
    return node.copy(content);
  };
  const nodes: any[] = [];
  slice.content.forEach((node) => nodes.push(convert(node)));
  return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
};

const pasteFromTextBoxKey = new PluginKey("pasteFromTextBoxPlugin");

export const PasteFromTextBoxExtension = Extension.create({
  name: "pasteFromTextBox",

  addProseMirrorPlugins() {
    const editor = this.editor;
    let preserveAnnotationMarksForCurrentPaste = false;
    return [
      new Plugin({
        key: pasteFromTextBoxKey,
        props: {
          clipboardSerializer: createClipboardHtmlSerializer(editor.schema),
          transformPastedHTML: html => {
            const wrapper = document.createElement("div");
            wrapper.innerHTML = html;
            normalizeClipboardTaskLists(wrapper);
            return wrapper.innerHTML;
          },
          transformPasted: (slice) => {
            const shouldPreserveAnnotations = preserveAnnotationMarksForCurrentPaste;
            preserveAnnotationMarksForCurrentPaste = false;
            return gatePastedTaskLists(transformPastedSliceForClipboard(slice, shouldPreserveAnnotations), editor.schema, !!editor.storage.taskList?.canWrite, shouldPreserveAnnotations);
          },
          handleDOMEvents: {
            cut: (view, event) => writeSelectionToInternalCutClipboard(view, event),
            paste: (_view, event) => {
              preserveAnnotationMarksForCurrentPaste = shouldPreserveAnnotationMarksFromClipboardData(event.clipboardData);
              return false;
            },
          },
          handlePaste: (view, event) => {
            if (!editor) return false;
            // TipTap reverses extension order when assembling plugins, so this
            // handler runs before PasteUrlAsLink's and would swallow a pasted
            // URL into plain text. Delegate first so an explicit URL pasted at
            // a caret becomes a link immediately (SW-10073).
            if (handleUrlPaste(view, event)) {
              return true;
            }
            const { clipboardData } = event;
            const shouldPreserveAnnotationMarks = shouldPreserveAnnotationMarksFromClipboardData(clipboardData);
            const rawHtml = clipboardData?.getData("text/html");
            const text = clipboardData?.getData("text/plain");
            const { state } = view;
            const sel = state.selection;
            const { $from } = sel;
            const parent = $from.parent;
            const parentIsTextblock = !!parent?.isTextblock;
            const parentTextLen = (parent?.textContent || "").length;
            const selectionNotEmpty = !sel.empty;
            const inlineContext = parentIsTextblock && (parentTextLen > 0 || selectionNotEmpty);

            if (rawHtml) {
              const html = extractClipboardHtmlFragment(rawHtml);
              const dom = typeof DOMParser !== "undefined" ? new DOMParser().parseFromString(html, "text/html") : null;
              const plain = text || dom?.body?.innerText || "";
              const normalizedPlain = plain.replace(/\r\n/g, "\n");
              if (shouldDeferToNativeHandling(html, dom)) {
                return false;
              }

              // When the clipboard HTML has no formatting of its own (bare
              // p/div/br wrappers, e.g. chat copy), markdown syntax in the
              // text is the real structure — render it instead of pasting
              // literal `#`/`- ` characters.
              const markdownHtml =
                !inlineContext && !shouldPreserveAnnotationMarks && !htmlHasRichFormatting(dom)
                  ? renderPastedMarkdownHtml(normalizedPlain)
                  : undefined;

              if (dom?.body) {
                try {
                  event.preventDefault();
                  const parser = getClipboardParser(view);
                  const fragmentWrapper = document.createElement("div");
                  fragmentWrapper.innerHTML = markdownHtml ?? html;
                  // Only unwrap non-semantic wrapper elements (like plain divs), preserve semantic elements like headings
                  if (fragmentWrapper.childElementCount === 1) {
                    const firstEl = fragmentWrapper.firstElementChild;
                    const tagName = firstEl?.tagName?.toUpperCase() || "";
                    // Only unwrap if it's a plain DIV without attributes - preserve headings and other semantic elements
                    const isPlainWrapper = tagName === "DIV" && firstEl?.getAttributeNames?.().length === 0;
                    if (isPlainWrapper) {
                      fragmentWrapper.innerHTML = firstEl?.innerHTML || fragmentWrapper.innerHTML;
                    }
                  }
                  normalizeClipboardTaskLists(fragmentWrapper);
                  let slice = gatePastedTaskLists(parser.parseSlice(fragmentWrapper, { preserveWhitespace: inlineContext }), view.state.schema, !!editor.storage.taskList?.canWrite, shouldPreserveAnnotationMarks);

                  if (!shouldPreserveAnnotationMarks) {
                    slice = stripSpacingArtifactsFromSlice(stripAnnotationMarksFromSlice(slice));
                  }

                  if (!selectionWithinList(view.state)) {
                    slice = closeSliceEdgesAroundLists(slice, view.state.schema);
                  }

                  const tr = view.state.tr.replaceSelection(slice);
                  view.dispatch(tr.scrollIntoView());
                  return true;
                } catch (error) {}
              }

              event.preventDefault();
              if (inlineContext) {
                insertInlineExact(editor, normalizedPlain);
              } else {
                const nodes = buildParagraphNodesFromPlainText(editor, normalizedPlain);
                editor.chain().focus().insertContent(nodes).run();
              }
              return true;
            }

            if (text) {
              const normalized = text.replace(/\r\n/g, "\n");
              event.preventDefault();
              if (inlineContext) {
                insertInlineExact(editor, normalized);
                return true;
              }

              // Plain-text-only clipboards can still carry markdown structure.
              const markdownHtml = renderPastedMarkdownHtml(normalized);
              if (markdownHtml && typeof document !== "undefined") {
                try {
                  const parser = getClipboardParser(view);
                  const wrapper = document.createElement("div");
                  wrapper.innerHTML = markdownHtml;
                  normalizeClipboardTaskLists(wrapper);
                  let slice = gatePastedTaskLists(stripSpacingArtifactsFromSlice(parser.parseSlice(wrapper)), view.state.schema, !!editor.storage.taskList?.canWrite, false);
                  if (!selectionWithinList(view.state)) {
                    slice = closeSliceEdgesAroundLists(slice, view.state.schema);
                  }
                  const tr = view.state.tr.replaceSelection(slice);
                  view.dispatch(tr.scrollIntoView());
                  return true;
                } catch (_) {}
              }

              const nodes = buildParagraphNodesFromPlainText(editor, normalized);
              editor.chain().focus().insertContent(nodes).run();
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});
